import * as THREE from 'three'
import Terrain from '../terrain'

const px = 0.0625

const shaftMaterial = new THREE.MeshStandardMaterial({
  color: 0x7a6045,
  roughness: 0.9,
  metalness: 0
})
const tipMaterial = new THREE.MeshStandardMaterial({
  color: 0xc9c9c9,
  roughness: 0.6,
  metalness: 0.2
})
const featherMaterial = new THREE.MeshStandardMaterial({
  color: 0xe8e4d8,
  roughness: 0.95,
  metalness: 0
})

const ARROW_GRAVITY = -20
const ARROW_SPEED = 20
const ARROW_LIFETIME = 5
const STUCK_LIFETIME = 0.7

interface Arrow {
  mesh: THREE.Group
  velocity: THREE.Vector3
  life: number
  stuck: boolean
  damage: number
}

const buildArrowMesh = () => {
  const group = new THREE.Group()
  const shaft = new THREE.Mesh(
    new THREE.BoxGeometry(px * 0.9, px * 0.9, px * 8),
    shaftMaterial
  )
  const tip = new THREE.Mesh(
    new THREE.BoxGeometry(px * 1.4, px * 1.4, px * 1.6),
    tipMaterial
  )
  tip.position.z = px * 4.6
  const feather = new THREE.Mesh(
    new THREE.BoxGeometry(px * 1.8, px * 1.8, px * 1.4),
    featherMaterial
  )
  feather.position.z = -px * 4
  group.add(shaft, tip, feather)
  return group
}

/**
 * Skeleton arrows: ballistic projectiles that collide with terrain and the
 * player. Meshes share materials; geometries are disposed on removal.
 */
export default class ProjectileManager {
  constructor(scene: THREE.Scene, terrain: Terrain) {
    this.scene = scene
    this.terrain = terrain
  }

  scene: THREE.Scene
  terrain: Terrain
  arrows: Arrow[] = []
  lookScratch = new THREE.Vector3()

  // launch toward a target point with ballistic arc compensation
  spawnArrow(from: THREE.Vector3, target: THREE.Vector3, damage = 5) {
    const dx = target.x - from.x
    const dy = target.y - from.y
    const dz = target.z - from.z
    const horizontal = Math.sqrt(dx * dx + dz * dz)
    if (horizontal < 0.01) return

    const flightTime = horizontal / ARROW_SPEED
    const vy = dy / flightTime - 0.5 * ARROW_GRAVITY * flightTime

    const mesh = buildArrowMesh()
    mesh.position.copy(from)
    this.scene.add(mesh)

    this.arrows.push({
      mesh,
      velocity: new THREE.Vector3(
        (dx / horizontal) * ARROW_SPEED,
        THREE.MathUtils.clamp(vy, -22, 22),
        (dz / horizontal) * ARROW_SPEED
      ),
      life: ARROW_LIFETIME,
      stuck: false,
      damage
    })
  }

  update(
    delta: number,
    target?: THREE.Object3D,
    onPlayerHit?: (amount: number) => void
  ) {
    for (let i = this.arrows.length - 1; i >= 0; i--) {
      const arrow = this.arrows[i]
      arrow.life -= delta
      if (arrow.life <= 0) {
        this.removeArrow(i)
        continue
      }
      if (arrow.stuck) continue

      arrow.velocity.y += ARROW_GRAVITY * delta
      arrow.mesh.position.x += arrow.velocity.x * delta
      arrow.mesh.position.y += arrow.velocity.y * delta
      arrow.mesh.position.z += arrow.velocity.z * delta

      // orient along the flight path
      this.lookScratch
        .copy(arrow.mesh.position)
        .add(arrow.velocity)
      arrow.mesh.lookAt(this.lookScratch)

      // player hit: camera eye is ~1.8 above the feet — test torso center
      if (target && onPlayerHit) {
        const dx = arrow.mesh.position.x - target.position.x
        const dy = arrow.mesh.position.y - (target.position.y - 0.7)
        const dz = arrow.mesh.position.z - target.position.z
        if (dx * dx + dz * dz < 0.6 && Math.abs(dy) < 1.3) {
          onPlayerHit(arrow.damage)
          this.removeArrow(i)
          continue
        }
      }

      // terrain hit: stick briefly, then vanish
      if (
        this.terrain.isSolidForMobAt(
          arrow.mesh.position.x,
          arrow.mesh.position.y,
          arrow.mesh.position.z
        )
      ) {
        arrow.stuck = true
        arrow.life = STUCK_LIFETIME
        arrow.velocity.set(0, 0, 0)
      }
    }
  }

  removeArrow(index: number) {
    const arrow = this.arrows[index]
    this.scene.remove(arrow.mesh)
    arrow.mesh.traverse(child => {
      if (child instanceof THREE.Mesh) child.geometry.dispose()
    })
    this.arrows.splice(index, 1)
  }

  clear() {
    for (let i = this.arrows.length - 1; i >= 0; i--) {
      this.removeArrow(i)
    }
  }
}
